import { dateTime, money } from "@/lib/format";
import type { LedgerEntry } from "@/lib/types";
import { Badge, Button, Table, cx } from "./ui";

/**
 * The statement.
 *
 * Two deliberate choices. Amounts are signed and coloured, so a debit reads as
 * a debit without anybody parsing a "direction" column. And the resulting
 * balance is shown on every row, because that is what a customer checks a
 * statement against and it is stored per entry precisely so it can be.
 */
export function LedgerTable({
  entries,
  onReverse,
  canReverse = false,
}: {
  entries: LedgerEntry[];
  onReverse?: (entry: LedgerEntry) => void;
  canReverse?: boolean;
}) {
  const head = ["Date", "Transaction", "Reference", "Amount", "Balance"];
  if (canReverse) head.push("");

  return (
    <Table head={head}>
      {entries.map((entry) => {
        const signed = entry.direction === "debit" ? -entry.amount_cents : entry.amount_cents;
        const reversed = Boolean(entry.reversed_by_id);
        return (
          <tr key={entry.id} className={cx(reversed && "bg-ink-50/60")}>
            <td className="ws-td whitespace-nowrap text-ink-500">{dateTime(entry.created_at)}</td>
            <td className="ws-td">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium">{entry.kind_label}</span>
                {entry.source === "admin" && <Badge tone="attention">manual</Badge>}
                {reversed && <Badge tone="neutral">reversed</Badge>}
                {entry.reverses_id && <Badge tone="neutral">reversal</Badge>}
              </div>
              {entry.description && <p className="mt-0.5 text-xs text-ink-500">{entry.description}</p>}
            </td>
            <td className="ws-td font-mono text-xs text-ink-500">{entry.reference || "-"}</td>
            <td
              className={cx(
                "ws-td whitespace-nowrap text-right font-semibold tabular-nums",
                signed < 0 ? "text-ink-900" : "text-signal-good",
              )}
            >
              {signed > 0 ? "+" : ""}
              {money(signed)}
            </td>
            <td className="ws-td whitespace-nowrap text-right tabular-nums text-ink-600">
              {money(entry.balance_after_cents)}
            </td>
            {canReverse && (
              <td className="ws-td text-right">
                {!reversed && !entry.reverses_id && onReverse && (
                  <Button size="sm" variant="secondary" onClick={() => onReverse(entry)}>
                    Reverse
                  </Button>
                )}
              </td>
            )}
          </tr>
        );
      })}
    </Table>
  );
}
